'use client';

import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface Service {
  icon: string;
  titleAr: string;
  titleEn: string;
  descriptionAr: string;
  descriptionEn: string;
}

const ServicesSection = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const services: Service[] = [
    {
      icon: 'ChatBubbleLeftRightIcon',
      titleAr: 'الاستشارات القانونية',
      titleEn: 'Legal Consultations',
      descriptionAr: 'تقديم رأي قانوني مكتوب وشفهي في مختلف الأنظمة.',
      descriptionEn: 'Providing written and oral legal opinions on various regulations.',
    },
    {
      icon: 'DocumentTextIcon',
      titleAr: 'صياغة ومراجعة العقود',
      titleEn: 'Contract Drafting and Review',
      descriptionAr: 'إعداد ومراجعة العقود بما يحفظ حقوق العميلة.',
      descriptionEn: 'Preparing and reviewing contracts to protect client rights.',
    },
    {
      icon: 'ScaleIcon',
      titleAr: 'التمثيل أمام المحاكم',
      titleEn: 'Court Representation',
      descriptionAr: 'مرافعة ومتابعة القضايا أمام الجهات القضائية.',
      descriptionEn: 'Pleading and following up on cases before judicial authorities.',
    },
    {
      icon: 'ShieldCheckIcon',
      titleAr: 'التوثيق والإنهاءات',
      titleEn: 'Documentation and Certifications',
      descriptionAr: 'إجراءات التوثيق، والإقرارات، والوكالات الشرعية.',
      descriptionEn: 'Authentication procedures, declarations, and legal agencies.',
    },
    {
      icon: 'HandRaisedIcon',
      titleAr: 'التحكيم والوساطة',
      titleEn: 'Arbitration and Mediation',
      descriptionAr: 'حل النزاعات بالطرق البديلة قبل الوصول للتقاضي.',
      descriptionEn: 'Resolving disputes through alternative methods before litigation.',
    },
  ];

  const content = {
    ar: {
      title: 'خدماتنا',
      subtitle: 'نقدم خدمات قانونية متخصصة للمرأة في المملكة',
    },
    en: {
      title: 'Our Services',
      subtitle: 'We provide specialized legal services for women in the Kingdom',
    },
  };

  const t = content[language];

  return (
    <section data-component-id="src/app/landing-page/components/ServicesSection.tsx:70:4" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="70" data-component-end-line="117" data-component-file="ServicesSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22services%22%2C%22className%22%3A%22py-20%20bg-surface%22%7D"
      id="services"
      className="py-20 bg-surface"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div data-component-id="src/app/landing-page/components/ServicesSection.tsx:75:6" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="75" data-component-end-line="116" data-component-file="ServicesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20lg%3Apx-10%22%7D" className="container mx-auto px-4 lg:px-10">
        {/* Section Header */}
        <div data-component-id="src/app/landing-page/components/ServicesSection.tsx:77:8" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="77" data-component-end-line="84" data-component-file="ServicesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%20animate-fade-in-up%22%7D" className="text-center mb-16 animate-fade-in-up">
          <h2 data-component-id="src/app/landing-page/components/ServicesSection.tsx:78:10" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="78" data-component-end-line="80" data-component-file="ServicesSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-3xl%20md%3Atext-4xl%20lg%3Atext-5xl%20text-primary%20mb-4%22%7D" className="font-playfair font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-4">
            {t.title}
          </h2>
          <p data-component-id="src/app/landing-page/components/ServicesSection.tsx:81:10" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="81" data-component-end-line="83" data-component-file="ServicesSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-inter%20text-lg%20md%3Atext-xl%20text-text-secondary%20max-w-3xl%20mx-auto%22%7D" className="font-inter text-lg md:text-xl text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div data-component-id="src/app/landing-page/components/ServicesSection.tsx:87:8" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="87" data-component-end-line="115" data-component-file="ServicesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%20max-w-6xl%20mx-auto%22%7D" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {services.map((service, index) => (
            <div data-component-id="src/app/landing-page/components/ServicesSection.tsx:89:12" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="89" data-component-end-line="113" data-component-file="ServicesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20p-8%20rounded-xl%20shadow-card%20hover%3Ashadow-xl%20transition-all%20duration-300%20border%20border-border%20hover%3Aborder-accent%20hover%3Ascale-105%20animate-fade-in-up%22%7D"
              key={index}
              className="bg-card p-8 rounded-xl shadow-card hover:shadow-xl transition-all duration-300 border border-border hover:border-accent hover:scale-105 animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Icon with bronze accent */}
              <div data-component-id="src/app/landing-page/components/ServicesSection.tsx:95:14" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="95" data-component-end-line="102" data-component-file="ServicesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20bg-accent%2F10%20rounded-xl%20flex%20items-center%20justify-center%20mb-6%20transition-all%20duration-300%20hover%3Ascale-110%22%7D" className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 hover:scale-110">
                <Icon data-component-id="src/app/landing-page/components/ServicesSection.tsx:96:16" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="96" data-component-end-line="101" data-component-file="ServicesSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-accent%22%7D"
                  name={service.icon as any}
                  size={32}
                  variant="solid"
                  className="text-accent"
                />
              </div>

              {/* Title */}
              <h3 data-component-id="src/app/landing-page/components/ServicesSection.tsx:105:14" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="105" data-component-end-line="107" data-component-file="ServicesSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-playfair%20font-semibold%20text-xl%20text-primary%20mb-3%22%7D" className="font-playfair font-semibold text-xl text-primary mb-3">
                {language === 'ar' ? service.titleAr : service.titleEn}
              </h3>

              {/* Description */}
              <p data-component-id="src/app/landing-page/components/ServicesSection.tsx:110:14" data-component-path="src/app/landing-page/components/ServicesSection.tsx" data-component-line="110" data-component-end-line="112" data-component-file="ServicesSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-inter%20text-sm%20text-text-secondary%20leading-relaxed%22%7D" className="font-inter text-sm text-text-secondary leading-relaxed">
                {language === 'ar' ? service.descriptionAr : service.descriptionEn}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;